import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import {
  getMyAvailability,
  updateMyAvailability,
  setBlackoutDates,
  removeBlackoutDate,
} from '../services/internApi';
import { DAYS_OF_WEEK, TIME_SLOTS } from '../utils/constants';

function emptyAvailability() {
  const a = {};
  DAYS_OF_WEEK.forEach((d) => {
    a[d.key] = {};
    TIME_SLOTS.forEach((s) => { a[d.key][s.key] = false; });
  });
  return a;
}

export default function AvailabilityPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [availability, setAvailability] = useState(emptyAvailability());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // B6: blackout dates state. Each entry: {id?, start_date, end_date, reason}.
  const [blackouts, setBlackouts] = useState([]);
  const [newBlackout, setNewBlackout] = useState({ start_date: '', end_date: '', reason: '' });
  const [savingBlackout, setSavingBlackout] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getMyAvailability()
      .then((data) => {
        const fresh = emptyAvailability();
        const saved = data.availability || {};
        DAYS_OF_WEEK.forEach((d) => {
          TIME_SLOTS.forEach((s) => {
            if (saved[d.key] && saved[d.key][s.key] != null) {
              fresh[d.key][s.key] = !!saved[d.key][s.key];
            }
          });
        });
        setAvailability(fresh);
        setBlackouts(Array.isArray(data.blackout_dates) ? data.blackout_dates : []);
      })
      .catch((err) => setError(err?.response?.data?.error || 'Failed to load availability'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (day, slot) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], [slot]: !prev[day][slot] },
    }));
  };

  const toggleDay = (day, value) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: Object.fromEntries(TIME_SLOTS.map((s) => [s.key, value])),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateMyAvailability(availability);
      setSnack({ open: true, message: 'Availability saved', severity: 'success' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save';
      setSnack({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const setAllDays = (value) => {
    const a = {};
    DAYS_OF_WEEK.forEach((d) => {
      a[d.key] = Object.fromEntries(TIME_SLOTS.map((s) => [s.key, value]));
    });
    setAvailability(a);
  };

  // B6 handlers
  const addBlackout = async () => {
    if (!newBlackout.start_date || !newBlackout.end_date) {
      setSnack({ open: true, message: 'Pick a start and end date', severity: 'error' });
      return;
    }
    if (newBlackout.start_date > newBlackout.end_date) {
      setSnack({ open: true, message: 'End date must be on or after start date', severity: 'error' });
      return;
    }
    setSavingBlackout(true);
    try {
      const next = [...blackouts, { ...newBlackout }];
      const res = await setBlackoutDates(next);
      setBlackouts(res.blackout_dates || next);
      setNewBlackout({ start_date: '', end_date: '', reason: '' });
      setSnack({ open: true, message: 'Time off added', severity: 'success' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to add time off';
      setSnack({ open: true, message: msg, severity: 'error' });
    } finally {
      setSavingBlackout(false);
    }
  };

  const deleteBlackout = async (id) => {
    setSavingBlackout(true);
    try {
      const res = await removeBlackoutDate(id);
      setBlackouts(res.blackout_dates || blackouts.filter((b) => b.id !== id));
      setSnack({ open: true, message: 'Time off removed', severity: 'success' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to remove time off';
      setSnack({ open: true, message: msg, severity: 'error' });
    } finally {
      setSavingBlackout(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Mobile layout: stacked card per day
  const mobileLayout = (
    <Stack spacing={1.5}>
      {DAYS_OF_WEEK.map((d) => {
        const dayAll = TIME_SLOTS.every((s) => availability[d.key][s.key]);
        const anySelected = TIME_SLOTS.some((s) => availability[d.key][s.key]);
        return (
          <Card key={d.key} variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{d.label}</Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Switch size="small" checked={dayAll} onChange={(e) => toggleDay(d.key, e.target.checked)} />}
                  label={<Typography variant="caption">All day</Typography>}
                  labelPlacement="start"
                />
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {TIME_SLOTS.map((s) => (
                  <FormControlLabel
                    key={s.key}
                    sx={{ flex: '1 1 30%', m: 0, border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}
                    control={<Switch size="small" checked={availability[d.key][s.key]} onChange={() => toggle(d.key, s.key)} />}
                    label={
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{s.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{s.range}</Typography>
                      </Box>
                    }
                  />
                ))}
              </Stack>
              {!anySelected && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  Not available this day
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  // Desktop layout: original table
  const desktopLayout = (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Day</TableCell>
          {TIME_SLOTS.map((s) => (
            <TableCell key={s.key} align="center">
              <Box>
                <Typography variant="subtitle2">{s.label}</Typography>
                <Typography variant="caption" color="text.secondary">{s.range}</Typography>
              </Box>
            </TableCell>
          ))}
          <TableCell align="center">All Day</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {DAYS_OF_WEEK.map((d) => {
          const dayAll = TIME_SLOTS.every((s) => availability[d.key][s.key]);
          return (
            <TableRow key={d.key}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.label}</Typography>
              </TableCell>
              {TIME_SLOTS.map((s) => (
                <TableCell key={s.key} align="center">
                  <Switch
                    checked={availability[d.key][s.key]}
                    onChange={() => toggle(d.key, s.key)}
                  />
                </TableCell>
              ))}
              <TableCell align="center">
                <Switch
                  checked={dayAll}
                  onChange={(e) => toggleDay(d.key, e.target.checked)}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>My Availability</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Set when you're generally available to work. Jobs whose time slot doesn't match your availability won't appear on your board.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={() => setAllDays(true)}>Select All</Button>
          <Button size="small" variant="outlined" onClick={() => setAllDays(false)}>Clear All</Button>
        </Box>

        {isMobile ? mobileLayout : desktopLayout}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={save}
            disabled={saving}
            fullWidth={isMobile}
          >
            {saving ? 'Saving...' : 'Save Availability'}
          </Button>
        </Box>
      </Paper>

      {/* B6: Time Off (vacation / blackout dates) */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <EventBusyIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Time Off</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Block dates when you're not available -- vacations, exams, anything that overrides your weekly schedule. New jobs in these date ranges won't appear on your board.
        </Typography>

        {blackouts.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
            No time off scheduled.
          </Typography>
        )}

        {blackouts.length > 0 && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {blackouts.map((b) => (
              <Card key={b.id || `${b.start_date}-${b.end_date}`} variant="outlined">
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {b.start_date} {b.start_date !== b.end_date && `to ${b.end_date}`}
                    </Typography>
                    {b.reason && (
                      <Typography variant="caption" color="text.secondary">{b.reason}</Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteBlackout(b.id)}
                    disabled={savingBlackout || !b.id}
                    aria-label="remove time off"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 2fr auto' },
          gap: 1.5,
          alignItems: 'start',
        }}>
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={newBlackout.start_date}
            onChange={(e) => setNewBlackout({ ...newBlackout, start_date: e.target.value })}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={newBlackout.end_date}
            onChange={(e) => setNewBlackout({ ...newBlackout, end_date: e.target.value })}
          />
          <TextField
            label="Reason (optional)"
            size="small"
            placeholder="Vacation, finals, etc."
            value={newBlackout.reason}
            onChange={(e) => setNewBlackout({ ...newBlackout, reason: e.target.value })}
            inputProps={{ maxLength: 200 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={addBlackout}
            disabled={savingBlackout}
            fullWidth={isMobile}
          >
            Add
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
